import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import { ColorField } from "./ColorField";
import { PreviewCard } from "./PreviewCard";
import type { BrandingFormState } from "../types";

interface BrandingTabProps {
  effectiveClientId: string | null;
  isLoading: boolean;
  error: unknown;
  formState: BrandingFormState;
  errors: Record<string, string>;
  onChange: (field: keyof BrandingFormState, value: string) => void;
  onSubmit: () => void;
  isSaving: boolean;
  isSaveError: boolean;
  isSaveSuccess: boolean;
  lightPreview: BrandingPreview;
  darkPreview: BrandingPreview;
}

interface BrandingPreview {
  background: string;
  text: string;
  primary: string;
  secondary: string;
  accent: string;
  chartPalette: string[];
  logoUrl?: string | null;
}

export const BrandingTab: React.FC<BrandingTabProps> = ({
  effectiveClientId,
  isLoading,
  error,
  formState,
  errors,
  onChange,
  onSubmit,
  isSaving,
  isSaveError,
  isSaveSuccess,
  lightPreview,
  darkPreview,
}) => (
  <SettingsSection>
    {!effectiveClientId && (
      <Alert severity="warning">Select a client to manage branding.</Alert>
    )}

    {Boolean(error) && (
      <Alert severity="error">
        Failed to load client branding. Please try again.
      </Alert>
    )}

    {!effectiveClientId ? null : isLoading ? (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading branding...</Typography>
      </Box>
    ) : (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Stack spacing={2.5}>
            <SectionCard
              title="Brand colours"
              description="Light and dark mode palettes for the UI."
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <SectionHeading>Light mode</SectionHeading>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Primary"
                          value={formState.primary_color}
                          error={errors.primary_color}
                          onChange={(value) => onChange("primary_color", value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Secondary"
                          value={formState.secondary_color}
                          error={errors.secondary_color}
                          onChange={(value) => onChange("secondary_color", value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Accent"
                          value={formState.accent_color}
                          error={errors.accent_color}
                          onChange={(value) => onChange("accent_color", value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Background"
                          value={formState.background_color}
                          error={errors.background_color}
                          onChange={(value) =>
                            onChange("background_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Text"
                          value={formState.text_color}
                          error={errors.text_color}
                          onChange={(value) => onChange("text_color", value)}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <SectionHeading>Dark mode</SectionHeading>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Primary"
                          value={formState.dark_primary_color}
                          error={errors.dark_primary_color}
                          onChange={(value) =>
                            onChange("dark_primary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Secondary"
                          value={formState.dark_secondary_color}
                          error={errors.dark_secondary_color}
                          onChange={(value) =>
                            onChange("dark_secondary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Accent"
                          value={formState.dark_accent_color}
                          error={errors.dark_accent_color}
                          onChange={(value) =>
                            onChange("dark_accent_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Background"
                          value={formState.dark_background_color}
                          error={errors.dark_background_color}
                          onChange={(value) =>
                            onChange("dark_background_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Text"
                          value={formState.dark_text_color}
                          error={errors.dark_text_color}
                          onChange={(value) =>
                            onChange("dark_text_color", value)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Chart colours"
              description="Primary chart accents and palettes."
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <SectionHeading>Light mode</SectionHeading>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart primary"
                          value={formState.chart_primary_color}
                          error={errors.chart_primary_color}
                          onChange={(value) =>
                            onChange("chart_primary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart secondary"
                          value={formState.chart_secondary_color}
                          error={errors.chart_secondary_color}
                          onChange={(value) =>
                            onChange("chart_secondary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart accent"
                          value={formState.chart_accent_color}
                          error={errors.chart_accent_color}
                          onChange={(value) =>
                            onChange("chart_accent_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Chart palette"
                          placeholder="#2a9d8f, #e9c46a, #f4a261"
                          value={formState.chart_palette}
                          onChange={(event) =>
                            onChange("chart_palette", event.target.value)
                          }
                          helperText={
                            errors.chart_palette ||
                            "Comma-separated hex colours (3-8)."
                          }
                          error={!!errors.chart_palette}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <SectionHeading>Dark mode</SectionHeading>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart primary"
                          value={formState.dark_chart_primary_color}
                          error={errors.dark_chart_primary_color}
                          onChange={(value) =>
                            onChange("dark_chart_primary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart secondary"
                          value={formState.dark_chart_secondary_color}
                          error={errors.dark_chart_secondary_color}
                          onChange={(value) =>
                            onChange("dark_chart_secondary_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <ColorField
                          label="Chart accent"
                          value={formState.dark_chart_accent_color}
                          error={errors.dark_chart_accent_color}
                          onChange={(value) =>
                            onChange("dark_chart_accent_color", value)
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Chart palette"
                          placeholder="#fa4616, #f58232, #c7c7c7"
                          value={formState.dark_chart_palette}
                          onChange={(event) =>
                            onChange("dark_chart_palette", event.target.value)
                          }
                          helperText={
                            errors.dark_chart_palette ||
                            "Comma-separated hex colours (3-8)."
                          }
                          error={!!errors.dark_chart_palette}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Logos"
              description="Upload or paste URLs for brand logos."
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Logo URL (light)"
                    value={formState.logo_url}
                    onChange={(event) => onChange("logo_url", event.target.value)}
                    placeholder="https://cdn.example.com/logo.svg"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Logo URL (dark)"
                    value={formState.logo_dark_url}
                    onChange={(event) =>
                      onChange("logo_dark_url", event.target.value)
                    }
                    placeholder="https://cdn.example.com/logo-dark.svg"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dark logo URL (override)"
                    value={formState.dark_logo_url}
                    onChange={(event) =>
                      onChange("dark_logo_url", event.target.value)
                    }
                    placeholder="https://cdn.example.com/logo-dark.svg"
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Stack>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!effectiveClientId || isSaving}
              startIcon={
                isSaving ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Save Branding
            </Button>
          </Box>

          {isSaveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to save branding. Please try again.
            </Alert>
          )}

          {isSaveSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Branding saved successfully.
            </Alert>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={2}>
            <PreviewCard title="Light preview" preview={lightPreview} />
            <PreviewCard title="Dark preview" preview={darkPreview} />
          </Stack>
        </Grid>
      </Grid>
    )}
  </SettingsSection>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary">
    {children}
  </Typography>
);
